import { useEffect, useState } from 'react';

import { useNavButtonPressedCallback, useNavLinkPressedCallback } from '@zenbusiness/application-commons-clickstream';
import Pill from '@zenbusiness/common-components/Pill';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';

import ButtonBase from '@mui/material/ButtonBase';
import Collapse from '@mui/material/Collapse';
import ListItem from '@mui/material/ListItem';
import Tooltip from '@mui/material/Tooltip';

import MenuItemIcon from './MenuItemIcon';

import {
  StyledButton,
  StyledExternalLink,
  StyledLink,
  StyledSubMenu,
  StyledText
} from '../styles';

/**
 * MenuItem definition.
 */
export const MenuItemProps = {
  eventProps: PropTypes.shape({
    experimentName: PropTypes.string,
    elementName: PropTypes.string,
    elementText: PropTypes.string,
    experimentVariation: PropTypes.string,
    navCategory: PropTypes.string,
    navIndex: PropTypes.number,
    pageCategory: PropTypes.string,
    pageSection: PropTypes.string
  }),
  icon: PropTypes.string,
  isBeta: PropTypes.bool,
  newItem: PropTypes.bool,
  onClick: PropTypes.func,
  onMobileClick: PropTypes.func,
  path: PropTypes.string,
  target: PropTypes.string,
  title: PropTypes.string.isRequired,
  tooltip: PropTypes.string,
  url: PropTypes.string
};

/**
 * MenuItem
 *
 * @param {{
 *  eventProps: object
 *  expanded?: boolean
 *  icon?: string
 *  isBeta?: boolean
 *  links?: MenuItem[]
 *  newItem?: boolean
 *  onClick?: Function
 *  onMobileClick: Function
 *  path?: string
 *  target?: string
 *  title: string
 *  tooltip?: string
 *  url?: string,
 * }} props
 *
 * @returns {import('react').ReactNode}
 */
export function MenuItem({
  eventProps,
  expanded,
  icon,
  isBeta,
  links,
  newItem,
  onClick,
  onMobileClick,
  path,
  target,
  title,
  tooltip,
  url
}) {
  const destination = path || url;

  const handleLinkTracking = useNavLinkPressedCallback({
    ...eventProps,
    linkDestination: destination
  });
  const handleMenuItemClick = () => {
    handleLinkTracking();
    onClick?.();
    onMobileClick?.();
    // Allow nav to shrink on mouseOut.
    document.activeElement.blur();
  };

  const menuItemLinkClass = classNames({ 'is-disabled': !!tooltip }, 'menu-item');
  const menuItemBody = (
    <>
      {!!icon && (
        <MenuItemIcon
          className="tile"
          src={icon}
        />
      )}

      <StyledText size="sm" tag="span">
        {title}
      </StyledText>

      {newItem && (
        <Pill
          className="new-flag"
          color="peaceBrand"
          label="New"
        />
      )}

      {isBeta && (
        <Pill
          className="new-flag"
          color="peaceBrand"
          label="BETA"
        />
      )}

    </>
  );

  // Return early if nothing to show.
  if (!destination && !links) {
    return null;
  }

  // Handle interally linked menu items.
  if (path) {
    return (
      <Tooltip title={tooltip}>
        <ListItem disablePadding>
          <StyledLink
            className={menuItemLinkClass}
            end
            onClick={handleMenuItemClick}
            to={path}
          >
            {menuItemBody}
          </StyledLink>
        </ListItem>
      </Tooltip>
    );
  }

  // Handle externally linked menu items.
  if (url) {
    return (
      <Tooltip title={tooltip}>
        <ListItem disablePadding>
          <StyledExternalLink
            className={menuItemLinkClass}
            href={url}
            onClick={handleMenuItemClick}
            target={target}
          >
            {menuItemBody}
          </StyledExternalLink>
        </ListItem>
      </Tooltip>
    );
  }

  // Handle expandable menu items.
  return (
    <ExpandableListItem
      eventProps={eventProps}
      expanded={expanded}
      menuItems={links}
      onClick={onClick}
      onMobileClick={onMobileClick}
    >
      {menuItemBody}
      <MenuItemIcon
        className="expand"
        onClick={onClick}
        src="arrow-sm-down-outline.svg"
      />
    </ExpandableListItem>
  );
}

MenuItem.propTypes = {
  ...MenuItemProps,
  expanded: PropTypes.bool,
  links: PropTypes.arrayOf(PropTypes.shape(MenuItemProps).isRequired)
};

/**
 * ExpandableListItem
 *
 * @param {{
 *  children: import('react').ReactNode
 *  eventProps: object
 *  expanded: boolean
 *  menuItems: [MenuItem]
 *  onClick?: Function
 *  onMobileClick?: Function
 * }} props
 * @returns {import('react').ReactNode}
 */
export function ExpandableListItem({
  children,
  eventProps,
  expanded,
  menuItems,
  onClick,
  onMobileClick
}) {
  const { pathname } = useLocation();
  const [active, setActive] = useState(false);

  const handleButtonTracking = useNavButtonPressedCallback(eventProps);
  const handleOnClickButton = () => {
    handleButtonTracking();
    onClick?.();
    document.activeElement.blur();
  };

  useEffect(() => {
    if (menuItems) {
      const parent = menuItems.some((item) => item?.path === pathname);
      setActive(parent);
    }
  }, [pathname, menuItems]);

  return (
    <ListItem disablePadding>
      <StyledButton
        className={classNames({ active, opened: expanded }, 'menu-item')}
        component={ButtonBase}
        onClick={handleOnClickButton}
        selected={expanded}
      >
        {children}
      </StyledButton>

      <Collapse in={expanded}>
        <StyledSubMenu component="ul">
          {menuItems.map((menuItem, idx) => (
            <MenuItem
              eventProps={{
                ...eventProps,
                elementText: menuItem.title,
                navIndex: idx + 1,
                navLevel: eventProps.navLevel + 1
              }}
              expanded={expanded}
              key={menuItem.title}
              onMobileClick={onMobileClick}
              {...menuItem}
            />
          ))}
        </StyledSubMenu>
      </Collapse>
    </ListItem>
  );
}

ExpandableListItem.propTypes = {
  children: PropTypes.node.isRequired,
  eventProps: MenuItemProps.eventProps,
  expanded: PropTypes.bool,
  menuItems: PropTypes.arrayOf(PropTypes.shape(MenuItemProps)).isRequired,
  onClick: PropTypes.func,
  onMobileClick: PropTypes.func
};
